/**
 * AdminLayout — shared shell for every /admin & /dashboard admin page.
 *
 * Pixel-perfect black-glass design system:
 *   • Root:         min-h-svh bg-black dark
 *   • Sidebar bg:   #050505 via CSS --sidebar-background override in index.css
 *   • Glass cards:  bg-white/[0.04] rounded-xl ring-1 ring-inset ring-white/[0.06]
 *   • Nav leaf:     h-8 text-[13px] · active → bg-white/10 font-medium ring-1 ring-inset ring-white/[0.08]
 *   • Nav group:    collapsible, same active pill, ChevronDown rotates
 *   • Sub-item:     h-7 text-[12px] · active → bg-white/[0.08] font-medium text-white
 *   • Header:       h-12 backdrop-blur-2xl bg-black/75 glass strip
 *   • Footer card:  glass, "Ver sitio" + optional Clerk UserButton
 *
 * Behaviour:
 *   • Collapsible icon-rail sidebar — state persisted via cookie
 *   • Tooltips on collapsed items, grouped IA
 *   • ⌘K command palette
 *   • Sticky header with route breadcrumbs
 *   • Mobile: off-canvas sheet
 *
 * Structure split:
 *   • AdminNavItems.tsx — sidebar nav items (leaf + groups)
 *   • AdminHeader.tsx   — sticky header + breadcrumbs
 *   • AdminStates.tsx   — AdminLoadingState / AdminSkeleton / route loading
 */

import { UserButton } from "@clerk/clerk-react";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import {
  ADMIN_SIDEBAR_NAV_GROUPS,
  PRINCIPAL_ADMIN_PATH,
  toFeatureKey,
  isAdminPathBlocked,
  type AdminSidebarNavItem,
} from "@/components/admin/adminSidebarNav";
import { AdminChunkErrorBoundary } from "@/components/admin/AdminChunkErrorBoundary";
import { AdminCommandPalette } from "@/components/admin/AdminCommandPalette";
import { AdminPageTransition } from "@/components/admin/AdminMotion";
import { AdminLoadingState, AdminSectionSkeleton } from "@/components/admin/AdminStates";
import { NavItemLeaf, NavItemWithChildren } from "@/components/admin/AdminNavItems";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { useAdminFeatureAccess } from "@/hooks/useAdminFeatureAccess";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { registerAdminErrorReporter } from "@/lib/reportAdminError";
import { preloadClerkForAdmin } from "@/lib/preloadClerkForAdmin";
import {
  loadAdminPendingQueues,
  peekAdminPendingQueues,
} from "@/lib/adminPendingQueues";
import { getAdminPanelVersion } from "@/lib/adminPanelVersion";
import { BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { useSEO } from "@/hooks/useSEO";
import { ExternalLink, Globe2 } from "lucide-react";
import { markGeoSimEligible, markPrivacyGateBypass } from "@/lib/geoSim";
import { useEffect, useMemo, useState, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useIsSuperAdmin, useClerkSessionClaims } from "@/lib/auth";
import { normalizeLanguageCode } from "@/lib/languages";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
} from "@/components/ui/sidebar";

// ─────────────────────────────────────────────────────────────────────────────
const ADMIN_SEO = {
  title: `Admin - ${BRAND_PERSON_NAME}`,
  description: "Panel de administración",
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}/admin`,
  robots: "noindex, nofollow" as const,
};

/** Read the shadcn sidebar:state cookie so collapse survives reloads. */
function readSidebarCookie(): boolean {
  if (typeof document === "undefined") return true;
  const match = document.cookie.match(/(?:^|;\s*)sidebar:state=([^;]+)/);
  return match ? match[1] === "true" : true;
}

// Nav items extracted to AdminNavItems.tsx for maintainability.

// Breadcrumbs + AdminHeader extracted to ./AdminHeader.tsx


// ── AdminLayout ───────────────────────────────────────────────────────────────

export function AdminLayout() {
  useSEO(ADMIN_SEO);
  const { t, i18n } = useTranslation();
  const [defaultOpen] = useState(readSidebarCookie);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const panelVersion = useMemo(() => getAdminPanelVersion(), []);

  // Badge states MUST be declared early, together with the other useState calls.
  // This guarantees:
  //  - Stable React hook call order on every render.
  //  - No TDZ when later code (useMemo deps, effects, etc.) references the badge values.
  // The useMemo below and the data effect will be after these declarations in source.
  const [pendingMembershipsBadge, setPendingMembershipsBadge] = useState<number | null>(null);
  const [pendingFreeMembersBadge, setPendingFreeMembersBadge] = useState<number | null>(null);

  const isSuperAdmin = useIsSuperAdmin();
  const { isLoaded: authClaimsLoaded } = useClerkSessionClaims();
  const { pathname } = useLocation();
  const currentLang = normalizeLanguageCode(i18n.resolvedLanguage ?? i18n.language ?? "");
  const viewSiteHref = `/?lang=${currentLang}`;

  // Carga de configuración de visibilidad (configurada por super admin en /admin/access-control).
  const { hiddenSet, isLoaded: featuresLoaded } = useAdminFeatureAccess();
  const { adminFetch, isSignedIn } = useAdminAuth();

  useEffect(() => {
    registerAdminErrorReporter(adminFetch);
  }, [adminFetch]);

  useEffect(() => {
    preloadClerkForAdmin();
    // Precalentar dashboard (ruta más visitada) mientras el usuario navega el shell.
    void import("@/pages/admin/AdminDashboard");
  }, []);

  // Usamos una key estable para el useMemo (evita recomputar en cada render).
  const hiddenKey = useMemo(() => [...hiddenSet].sort().join("|"), [hiddenSet]);

  // Sidebar: 
  // - Super admin: ve absolutamente todo.
  // - Admin regular: solo ve lo que el super admin no haya ocultado + nunca ve las super-only.
  //   El filtrado respeta la config dinámica de /admin/access-control.
  const navGroups = useMemo(
    () =>
      ADMIN_SIDEBAR_NAV_GROUPS.map((group) => {
        const filteredItems = group.items
          .filter((item) => {
            if (isSuperAdmin) return true;
            return !isAdminPathBlocked(item.to, hiddenSet);
          })
          .map((item) => {
            // Also filter any children (for collapsible groups) so hidden sub-items don't leak in sidebar
            if (!item.children || isSuperAdmin) return item;
            const visibleChildren = item.children.filter(
              (child) => !isAdminPathBlocked(child.to, hiddenSet),
            );
            return { ...item, children: visibleChildren } as typeof item;
          })
          .filter((item) => {
            // If an item had only children and they were all hidden, drop the parent too
            if (item.children && item.children.length === 0) return false;
            return true;
          });

        return { ...group, items: filteredItems };
      }).filter((group) => group.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuperAdmin, hiddenKey],
  );

  // Inject dynamic badges for membership/dual coherence (pending counts)
  const navGroupsWithBadges = useMemo(() => {
    return navGroups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        if (item.to === "/admin/memberships") {
          return { ...item, badge: pendingMembershipsBadge && pendingMembershipsBadge > 0 ? pendingMembershipsBadge : item.badge };
        }
        if (item.to === "/admin/free-members") {
          return { ...item, badge: pendingFreeMembersBadge && pendingFreeMembersBadge > 0 ? pendingFreeMembersBadge : item.badge };
        }
        // Example for dual: could add count of active dual slots if API, but for now static
        return item;
      }),
    }));
  }, [navGroups, pendingMembershipsBadge, pendingFreeMembersBadge]);

  // Guard de ruta directa + visibilidad dinámica controlada por super admin.
  // - Esperamos claims de Clerk + config de /admin/access-control para regular admins.
  // - Un admin regular solo ve y accede a lo que el super admin le dejó visible.
  // - Super admin siempre ve todo (bypass total).
  const featuresReady = featuresLoaded;
  const waitingForAuth = !isSuperAdmin && (!authClaimsLoaded || !featuresReady);

  const regularAdminBlocked =
    !isSuperAdmin &&
    authClaimsLoaded &&
    featuresReady &&
    isAdminPathBlocked(pathname, hiddenSet);

  // Destino seguro para regular admin cuando intenta acceder a algo bloqueado.
  // Preferimos la feature principal; si está oculta también, caemos a /admin (siempre visible).
  const safeLandingForRegular =
    !isAdminPathBlocked(PRINCIPAL_ADMIN_PATH, hiddenSet)
      ? PRINCIPAL_ADMIN_PATH
      : "/admin";

  // ⌘K / Ctrl+K → command palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!isSignedIn || !adminFetch) return;
    let cancelled = false;
    // Shared cache with AdminDashboard — one pair of requests, not two.
    const peek = peekAdminPendingQueues();
    if (peek) {
      setPendingMembershipsBadge(peek.memberships);
      setPendingFreeMembersBadge(peek.free);
    }
    void loadAdminPendingQueues(adminFetch).then((q) => {
      if (cancelled) return;
      setPendingMembershipsBadge(q.memberships);
      setPendingFreeMembersBadge(q.free);
    });
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, adminFetch]);

  return (
    <div className="admin-app-shell dark min-h-svh bg-black">
      <SidebarProvider defaultOpen={defaultOpen}>
        {/* ── Sidebar ──────────────────────────────────────────────────────── */}
        <Sidebar
          collapsible="icon"
          className="border-r-0 [&_[data-sidebar=sidebar]]:border-r [&_[data-sidebar=sidebar]]:border-white/[0.05]"
        >
          {/* ── Brand card ── */}
          <SidebarHeader className="border-b border-white/[0.05] px-2 py-2.5">
            <div className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-2.5 py-[7px] ring-1 ring-inset ring-white/[0.06]">
              {/* Avatar initial */}
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-[13px] font-bold leading-none tracking-tight text-black shadow-sm">
                {(BRAND_PERSON_NAME || "A").charAt(0).toUpperCase()}
              </div>
              {/* Text block — hidden in icon-rail mode */}
              <div className="min-w-0 group-data-[collapsible=icon]:hidden">
                <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-white/25">
                  {t("adminShell.site")}
                </p>
                <p className="truncate text-[12.5px] font-semibold leading-snug text-white/85">
                  {t("adminShell.panel")}
                </p>
              </div>
            </div>
          </SidebarHeader>

          {/* ── Nav groups ── */}
          <SidebarContent className="gap-0 px-2 py-2">
            {navGroupsWithBadges.map((group, i) => (
              <SidebarGroup
                key={group.label}
                className={cn("py-1", i > 0 && "mt-1 border-t border-white/[0.04] pt-2")}
              >
                <SidebarGroupLabel className="mb-1 flex h-5 items-center px-1.5 text-[9px] font-semibold uppercase tracking-[0.2em] text-white/25">
                  {group.label}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="gap-px">
                    {group.items.map((item) =>
                      item.children ? (
                        <NavItemWithChildren
                          key={item.to}
                          item={
                            item as AdminSidebarNavItem & {
                              children: NonNullable<AdminSidebarNavItem["children"]>;
                            }
                          }
                        />
                      ) : (
                        <NavItemLeaf key={item.to} item={item} />
                      ),
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>

          {/* ── Footer card ── */}
          <SidebarFooter className="border-t border-white/[0.05] p-2">
            <div className="overflow-hidden rounded-xl bg-white/[0.03] ring-1 ring-inset ring-white/[0.06]">
              {/* External link + geo sim entry (marks localStorage so new tabs see toolbar) */}
              <SidebarMenu className="p-1">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip={t("adminShell.viewSitePublic")}
                    className="h-8 rounded-lg text-[13px] font-normal text-white/40 transition-colors duration-150 hover:bg-white/[0.06] hover:text-white/75"
                  >
                    <a
                      href={viewSiteHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        markGeoSimEligible();
                        markPrivacyGateBypass();
                      }}
                    >
                      <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                      <span>{t("adminShell.viewSite")}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip="Simular visitante por país (geo real en edge)"
                    className="h-8 rounded-lg text-[13px] font-normal text-violet-300/70 transition-colors duration-150 hover:bg-violet-500/15 hover:text-violet-200"
                  >
                    <a
                      href="/?geo_sim_boot=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        markGeoSimEligible();
                        markPrivacyGateBypass();
                      }}
                    >
                      <Globe2 className="h-4 w-4 shrink-0" aria-hidden />
                      <span>Simular visitante</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>

              {/* Clerk UserButton — only when Clerk is configured */}
              {getClerkPublishableKey() && (
                <div className="flex items-center gap-2.5 border-t border-white/[0.05] px-3 py-[7px]">
                  <UserButton afterSignOutUrl="/" />
                  <span className="text-[11px] text-white/30 group-data-[collapsible=icon]:hidden">
                    {t("adminShell.myAccount")}
                  </span>
                </div>
              )}

              {/* Build stamp — confirm deploy without noisy UI */}
              <div
                className="border-t border-white/[0.05] px-3 py-1.5 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-1"
                title={panelVersion.title}
              >
                <p className="truncate font-mono text-[9px] leading-tight tracking-wide text-white/20 group-data-[collapsible=icon]:text-center">
                  <span className="group-data-[collapsible=icon]:hidden">
                    {panelVersion.label}
                  </span>
                  <span className="hidden group-data-[collapsible=icon]:inline">
                    {panelVersion.shortSha.slice(0, 4)}
                  </span>
                </p>
              </div>
            </div>
          </SidebarFooter>

          <SidebarRail className="hover:bg-white/[0.04] after:bg-white/[0.06]" />
        </Sidebar>

        {/* ── Main content ─────────────────────────────────────────────────── */}
        <SidebarInset className="admin-page min-w-0 bg-black text-zinc-100">
          <AdminHeader onOpenPalette={() => setPaletteOpen(true)} />
          <div className="mx-auto w-full max-w-7xl px-4 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] pt-5 sm:px-6 sm:pb-10 lg:px-8">
            {waitingForAuth ? (
              <AdminLoadingState full label={t("adminShell.loadingPermissions") || "Cargando permisos…"} />
            ) : regularAdminBlocked ? (
              <Navigate to={safeLandingForRegular} replace />
            ) : (
              // resetKey=pathname so a failed lazy page does not stick when navigating away
              <AdminChunkErrorBoundary resetKey={pathname}>
                <Suspense fallback={<AdminSectionSkeleton />}>
                  <AdminPageTransition>
                    <Outlet />
                  </AdminPageTransition>
                </Suspense>
              </AdminChunkErrorBoundary>
            )}
          </div>
        </SidebarInset>

        <AdminCommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          navGroups={navGroupsWithBadges}
        />
      </SidebarProvider>
    </div>
  );
}
